import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import useUsageLimits from '../hooks/useUsageLimits';
import UpgradeModal from '../components/UpgradeModal';
import AuthModal from '../components/AuthModal';
import { Calendar, Sparkles, Download, Loader, ChefHat, ShoppingCart, Coffee, Sun, Moon, Apple, Check, X, FileText, Lock } from 'lucide-react';

// API sécurisée via Supabase Edge Function
const SUPABASE_URL = 'https://ififowdcasczwlydunfj.supabase.co';
const OPENAI_PROXY = `${SUPABASE_URL}/functions/v1/openai-proxy`;

const MealPlanPage = () => {
  const { user, profile, isAuthenticated } = useAuth();
  const { canUse, getLimit, incrementUsage, isPro } = useUsageLimits();
  
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [preferences, setPreferences] = useState({ allergies: [], dislikes: '', budget: 'medium', cookingTime: 'medium', diet: 'balanced' });

  // Limites
  const planLimit = getLimit('mealplan');
  const canGenerate = canUse('mealplan');

  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const allergiesOptions = ['Gluten', 'Lactose', 'Arachides', 'Fruits à coque', 'Œufs', 'Poisson', 'Soja'];
  const dietOptions = [
    { id: 'balanced', label: 'Équilibré', emoji: '🥗' },
    { id: 'lowcarb', label: 'Low Carb', emoji: '🥩' },
    { id: 'vegetarian', label: 'Végétarien', emoji: '🥬' },
    { id: 'highprotein', label: 'Hyperprotéiné', emoji: '💪' },
    { id: 'mediterranean', label: 'Méditerranéen', emoji: '🫒' }
  ];

  const loadPlan = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.from('meal_plans').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(1).single();
    if (data) setCurrentPlan(data);
  }, [user]);

  useEffect(() => { if (isAuthenticated) loadPlan(); }, [loadPlan, isAuthenticated]);

  const generatePlan = async () => {
    // Vérifier authentification et limites
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!canGenerate) {
      setShowUpgradeModal(true);
      return;
    }
    
    setIsGenerating(true);
    
    // Incrémenter l'usage
    incrementUsage('mealplan');
    
    const dailyCals = profile?.daily_calories || 2000;
    const dietLabels = {
      balanced: 'équilibré',
      lowcarb: 'pauvre en glucides (low carb)',
      vegetarian: 'végétarien',
      highprotein: 'riche en protéines',
      mediterranean: 'méditerranéen'
    };
    
    try {
      // Appel via le proxy sécurisé Supabase
      const response = await fetch(OPENAI_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `Tu es Louis, nutritionniste français expert. Tu crées des plans de repas PERSONNALISÉS et RÉALISTES avec des recettes françaises faciles.

RÈGLES STRICTES:
- Calories totales par jour: environ ${dailyCals} kcal
- Petit-déjeuner: ~${Math.round(dailyCals * 0.25)} kcal
- Déjeuner: ~${Math.round(dailyCals * 0.35)} kcal  
- Collation: ~${Math.round(dailyCals * 0.10)} kcal
- Dîner: ~${Math.round(dailyCals * 0.30)} kcal
- Recettes simples, ingrédients trouvables en supermarché français
- Variété: pas les mêmes plats qui se répètent

Tu dois répondre UNIQUEMENT avec un JSON valide, sans texte avant ou après.`
            },
            {
              role: 'user',
              content: `Crée un plan de repas sur 7 jours avec ces critères:
- Régime: ${dietLabels[preferences.diet] || 'équilibré'}
- Allergies à ÉVITER: ${preferences.allergies.length > 0 ? preferences.allergies.join(', ') : 'aucune'}
- Aliments NON AIMÉS: ${preferences.dislikes || 'aucun'}
- Budget: ${preferences.budget === 'low' ? 'économique' : preferences.budget === 'high' ? 'sans limite' : 'modéré'}
- Temps de cuisine: ${preferences.cookingTime === 'quick' ? '15 min max' : preferences.cookingTime === 'long' ? 'pas de limite' : '30 min max'}

Réponds avec ce JSON exact:
{
  "title": "Mon Plan ${dietLabels[preferences.diet] || 'Équilibré'} 7 Jours",
  "days": [
    {
      "day": "Lundi",
      "meals": {
        "breakfast": {"name": "Nom du petit-déj", "desc": "Description courte", "calories": 400},
        "lunch": {"name": "Nom du déjeuner", "desc": "Description courte", "calories": 600},
        "snack": {"name": "Nom collation", "desc": "Description", "calories": 150},
        "dinner": {"name": "Nom du dîner", "desc": "Description courte", "calories": 500}
      },
      "totalCalories": 1650
    }
  ],
  "shoppingList": {
    "Fruits & Légumes": ["tomates", "salade", "pommes"],
    "Protéines": ["poulet", "oeufs", "saumon"],
    "Féculents": ["riz", "pâtes", "pain complet"],
    "Produits laitiers": ["yaourt", "fromage"],
    "Épicerie": ["huile olive", "épices"]
  },
  "tips": ["Conseil 1 personnalisé", "Conseil 2 personnalisé", "Conseil 3 personnalisé"]
}

IMPORTANT: Génère les 7 jours (Lundi à Dimanche) avec des repas DIFFÉRENTS chaque jour!`
            }
          ],
          max_tokens: 4000,
          temperature: 0.8
        })
      });

      const data = await response.json();
      
      if (data.error) {
        console.error('Erreur OpenAI:', data.error);
        alert('Erreur lors de la génération. Veuillez réessayer dans quelques secondes.');
        return;
      }
      
      if (data.choices?.[0]) {
        const content = data.choices[0].message.content;
        console.log('Réponse OpenAI:', content);
        
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const plan = JSON.parse(jsonMatch[0]);
            
            if (!plan.days || plan.days.length < 7) {
              alert('Le plan généré est incomplet. Veuillez réessayer !');
              return;
            }
            
            const { data: saved, error } = await supabase
              .from('meal_plans')
              .insert({ user_id: user.id, plan_data: plan, preferences })
              .select()
              .single();
              
            if (error) {
              console.error('Erreur Supabase:', error);
              // Afficher quand même le plan même si pas sauvegardé
              setCurrentPlan({ plan_data: plan, created_at: new Date().toISOString() });
            } else if (saved) {
              setCurrentPlan(saved);
            }
          } catch (parseErr) {
            console.error('Erreur parsing JSON:', parseErr);
            alert('Erreur de format. Veuillez réessayer !');
          }
        } else {
          alert('Réponse invalide. Veuillez réessayer !');
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur de connexion. Vérifiez votre connexion internet.');
    } finally {
      setIsGenerating(false);
      setShowPreferences(false);
    }
  };

  const generatePDF = () => {
    if (!currentPlan?.plan_data) return;
    const plan = currentPlan.plan_data;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${plan.title}</title><style>body{font-family:system-ui;padding:40px;max-width:800px;margin:auto}h1{color:#3b82f6}h2{color:#1e40af;border-bottom:2px solid #3b82f6;padding-bottom:8px;margin-top:30px}.meal{background:#f8fafc;padding:15px;margin:10px 0;border-radius:8px;border-left:4px solid #3b82f6}.meal strong{color:#1e40af}.calories{color:#f59e0b;font-weight:600}.shopping{columns:2}.shopping li{padding:4px 0}</style></head><body><h1>🥗 ${plan.title}</h1><p>Préparé par Louis, votre coach nutrition</p>${plan.days?.map(day => `<h2>📅 ${day.day} (${day.totalCalories} kcal)</h2><div class="meal"><strong>☕ Petit-déjeuner:</strong> ${day.meals.breakfast.name}<br><small>${day.meals.breakfast.desc}</small> <span class="calories">${day.meals.breakfast.calories} kcal</span></div><div class="meal"><strong>🌞 Déjeuner:</strong> ${day.meals.lunch.name}<br><small>${day.meals.lunch.desc}</small> <span class="calories">${day.meals.lunch.calories} kcal</span></div><div class="meal"><strong>🍎 Collation:</strong> ${day.meals.snack.name} <span class="calories">${day.meals.snack.calories} kcal</span></div><div class="meal"><strong>🌙 Dîner:</strong> ${day.meals.dinner.name}<br><small>${day.meals.dinner.desc}</small> <span class="calories">${day.meals.dinner.calories} kcal</span></div>`).join('') || ''}<h2>🛒 Liste de courses</h2><ul class="shopping">${Object.entries(plan.shoppingList || {}).map(([cat, items]) => items.map(item => `<li>☐ ${item}</li>`).join('')).join('')}</ul><h2>💡 Conseils</h2><ul>${plan.tips?.map(t => `<li>${t}</li>`).join('') || ''}</ul><p style="margin-top:40px;color:#888;text-align:center">Préparé avec soin par MonCoach.eu</p></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const plan = currentPlan?.plan_data;
  const todayPlan = plan?.days?.[selectedDay];

  // Si non connecté, afficher invitation à s'inscrire
  if (!isAuthenticated) {
    return (
      <div className="mealplan-page">
        <div className="locked-feature-card">
          <div className="locked-emoji">📋</div>
          <h1>Plans de Repas Personnalisés</h1>
          <p>Recevez un menu sur-mesure pour 7 jours avec votre liste de courses incluse !</p>
          <div className="locked-benefits">
            <div className="benefit-highlight">🎁 <strong>1 plan complet par mois OFFERT</strong></div>
            <div>✓ Menu 7 jours adapté à vos goûts</div>
            <div>✓ Liste de courses prête à imprimer</div>
            <div>✓ Téléchargeable pour l'avoir partout</div>
          </div>
          <button className="primary-btn" onClick={() => setShowAuthModal(true)}>
            <Sparkles size={20} />
            Créer mon compte gratuit
          </button>
          <p className="locked-subtext">Inscription en 30 secondes • Sans engagement • Sans carte bancaire</p>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  return (
    <div className="mealplan-page">
      <div className="page-header">
        <div>
          <h1>📋 Plans de Repas</h1>
          <p>Votre menu personnalisé sur 7 jours par Louis</p>
          {!isPro && (
            <span className="usage-badge">
              {planLimit.isUnlimited ? '∞' : planLimit.remaining} plan restant ce mois
              {planLimit.remaining === 0 && !planLimit.isUnlimited && <Lock size={14} />}
            </span>
          )}
        </div>
        <div className="header-btns">
          {currentPlan && <button className="secondary-btn" onClick={generatePDF}><Download size={18} /> Télécharger</button>}
          <button className="primary-btn" onClick={() => setShowPreferences(true)} disabled={isGenerating || (!canGenerate && !isPro)}>
            {isGenerating ? <><Loader size={20} className="spin" /> Génération...</> : <><Sparkles size={20} /> {currentPlan ? 'Nouveau plan' : 'Générer'}</>}
          </button>
        </div>
      </div>

      {!currentPlan ? (
        <div className="empty-state-large">
          <ChefHat size={64} />
          <h2>Votre plan personnalisé vous attend !</h2>
          <p>Louis va créer un menu sur 7 jours adapté à vos objectifs, vos goûts et votre emploi du temps. Avec liste de courses incluse !</p>
          <button className="primary-btn large" onClick={() => setShowPreferences(true)} disabled={!canGenerate && !isPro}>
            <Sparkles size={20} /> Créer mon plan personnalisé
          </button>
          <div className="features-row">
            <div className="feature-item"><Calendar size={24} /><span>7 jours complets</span></div>
            <div className="feature-item"><ShoppingCart size={24} /><span>Liste de courses</span></div>
            <div className="feature-item"><FileText size={24} /><span>Téléchargeable</span></div>
          </div>
        </div>
        ) : (
          <>
            <div className="plan-header-banner">
              <div>
                <h2>{plan?.title || 'Mon plan'}</h2>
                <span>Créé le {new Date(currentPlan.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>

            <div className="days-tabs">
              {days.map((day, i) => (
                <button key={day} className={`day-tab ${selectedDay === i ? 'active' : ''}`} onClick={() => setSelectedDay(i)}>
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>

            {todayPlan && (
              <div className="day-content">
                <div className="day-title">
                  <h3>{todayPlan.day}</h3>
                  <span className="day-cals">{todayPlan.totalCalories} kcal</span>
                </div>
                <div className="meals-grid">
                  <MealCard icon={<Coffee />} type="Petit-déjeuner" meal={todayPlan.meals.breakfast} />
                  <MealCard icon={<Sun />} type="Déjeuner" meal={todayPlan.meals.lunch} />
                  <MealCard icon={<Apple />} type="Collation" meal={todayPlan.meals.snack} />
                  <MealCard icon={<Moon />} type="Dîner" meal={todayPlan.meals.dinner} />
                </div>
              </div>
            )}

            {plan?.shoppingList && (
              <div className="card">
                <h2><ShoppingCart size={20} /> Liste de courses</h2>
                <div className="shopping-grid">
                  {Object.entries(plan.shoppingList).map(([cat, items]) => (
                    <div key={cat} className="shopping-cat">
                      <h4>{cat}</h4>
                      <ul>{items.map((item, i) => <li key={i}><Check size={14} /> {item}</li>)}</ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {plan?.tips && (
              <div className="tips-banner">
                <h3><Sparkles size={20} /> Conseils de Louis</h3>
                <ul>{plan.tips.map((tip, i) => <li key={i}>💡 {tip}</li>)}</ul>
              </div>
            )}
          </>
        )}

        {showPreferences && (
          <PreferencesModal
            preferences={preferences}
            setPreferences={setPreferences}
            onClose={() => setShowPreferences(false)}
            onGenerate={generatePlan}
            allergiesOptions={allergiesOptions}
            dietOptions={dietOptions}
            isGenerating={isGenerating}
          />
        )}
        
        {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
  );
};

const MealCard = ({ icon, type, meal }) => (
  <div className="meal-plan-card">
    <div className="meal-card-head">{icon}<span>{type}</span><span className="meal-cals">{meal.calories} kcal</span></div>
    <h4>{meal.name}</h4>
    <p>{meal.desc}</p>
  </div>
);

const PreferencesModal = ({ preferences, setPreferences, onClose, onGenerate, allergiesOptions, dietOptions, isGenerating }) => {
  const toggleAllergy = (a) => setPreferences(p => ({ ...p, allergies: p.allergies.includes(a) ? p.allergies.filter(x => x !== a) : [...p.allergies, a] }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Personnalisez votre plan</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={24} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Type de régime</label>
            <div className="diet-grid">
              {dietOptions.map(d => (
                <button key={d.id} className={`diet-btn ${preferences.diet === d.id ? 'active' : ''}`} onClick={() => setPreferences(p => ({ ...p, diet: d.id }))}>
                  <span>{d.emoji}</span><span>{d.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Allergies / Intolérances</label>
            <div className="allergy-grid">
              {allergiesOptions.map(a => (
                <button key={a} className={`allergy-btn ${preferences.allergies.includes(a) ? 'active' : ''}`} onClick={() => toggleAllergy(a)}>
                  {preferences.allergies.includes(a) && <Check size={14} />} {a}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Aliments que tu n'aimes pas</label>
            <input type="text" value={preferences.dislikes} onChange={e => setPreferences(p => ({ ...p, dislikes: e.target.value }))} placeholder="Ex: brocolis, champignons..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Budget</label>
              <select value={preferences.budget} onChange={e => setPreferences(p => ({ ...p, budget: e.target.value }))}>
                <option value="low">Économique</option>
                <option value="medium">Modéré</option>
                <option value="high">Sans limite</option>
              </select>
            </div>
            <div className="form-group">
              <label>Temps de cuisine</label>
              <select value={preferences.cookingTime} onChange={e => setPreferences(p => ({ ...p, cookingTime: e.target.value }))}>
                <option value="quick">Rapide (15min)</option>
                <option value="medium">Moyen (30min)</option>
                <option value="long">Pas de limite</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Annuler</button>
          <button onClick={onGenerate} disabled={isGenerating} className="primary-btn">
            {isGenerating ? <><Loader size={18} className="spin" /> Génération...</> : <><Sparkles size={18} /> Générer mon plan</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealPlanPage;
